import {
  ClipboardList,
  ShieldCheck,
  Container,
  Activity,
  Clock,
  PackageCheck,
  ChevronRight,
} from "lucide-react";

const STEPS = [
  {
    icon: ClipboardList,
    title: "Book Shipment",
    desc: "Submit a booking request inside FreightIQ™.",
  },
  {
    icon: ShieldCheck,
    title: "Carrier Confirmation",
    desc: "Space confirmed across MSC, Maersk, CMA CGM and partners.",
  },
  {
    icon: Container,
    title: "Container Allocation",
    desc: "20GP, 40GP or 40HQ allocated with full equipment ID.",
  },
  {
    icon: Activity,
    title: "Milestone Tracking",
    desc: "Live events from gate-in to vessel departure.",
  },
  {
    icon: Clock,
    title: "ETA Monitoring",
    desc: "Auto-updating ETA as vessel position changes.",
  },
  {
    icon: PackageCheck,
    title: "Delivered",
    desc: "Proof of delivery and documents archived in Workspace.",
  },
];

export default function ProcessFlow() {
  return (
    <section
      id="freightiq"
      data-testid="process-flow-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-xs tracking-[0.18em] uppercase font-bold text-[#3B82F6]">
            How It Works
          </div>
          <h2 className="mt-3 font-display font-extrabold text-4xl md:text-5xl tracking-[-0.02em] text-[#0B1F3A]">
            How FreightIQ™ works.
          </h2>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            Six connected steps — one continuous flow — from booking request to
            final delivery.
          </p>
        </div>

        {/* Desktop: horizontal grid */}
        <div className="hidden lg:block mt-16 relative">
          <div className="absolute top-9 left-12 right-12 dmx-dashed-line h-px" />
          <div className="relative grid grid-cols-6 gap-4">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`process-step-${idx}`}
                  className="flex flex-col items-center text-center"
                >
                  <div className="relative h-[72px] w-[72px] rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-[#0B1F3A] hover:border-[#3B82F6]/40 hover:shadow-[0_10px_30px_-15px_rgba(59,130,246,0.3)] transition-all">
                    <Icon className="h-6 w-6" strokeWidth={1.75} />
                    <span className="absolute -top-2 -right-2 font-mono text-[10px] text-slate-400 bg-white border border-slate-200 rounded-full px-1.5 py-0.5">
                      0{idx + 1}
                    </span>
                  </div>
                  <div className="mt-5 font-display font-bold text-[#0B1F3A] tracking-tight">
                    {s.title}
                  </div>
                  <div className="mt-1.5 text-sm text-slate-500 max-w-[180px]">
                    {s.desc}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile/tablet: vertical */}
        <div className="lg:hidden mt-12 relative pl-6">
          <div className="absolute top-2 bottom-2 left-[26px] dmx-dashed-line-vertical w-px" />
          <div className="space-y-7">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="relative flex gap-4">
                  <div className="relative -ml-2 z-10 h-11 w-11 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-[#0B1F3A] shrink-0">
                    <Icon className="h-4.5 w-4.5 h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div className="pt-1.5">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-slate-400">
                        0{idx + 1}
                      </span>
                      <div className="font-display font-bold text-[#0B1F3A] tracking-tight">
                        {s.title}
                      </div>
                    </div>
                    <div className="mt-1 text-sm text-slate-500 leading-relaxed">
                      {s.desc}
                    </div>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <ChevronRight className="hidden h-4 w-4 text-slate-300" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
